// ============================================================================
// apps/web/src/components/AppShell.tsx
// ----------------------------------------------------------------------------
// Shell pour les pages authentifiées :
//   - Top bar : (mobile) bouton hamburger + logo + nom utilisateur + Réglages
//     + Déconnexion. (Le bouton "Mot de passe" est accessible depuis Réglages.)
//   - Navigation entre modules :
//       · Desktop : sidebar gauche qui apparaît au survol d'une bande primary.
//       · Mobile  : tiroir coulissant ouvert par le bouton hamburger (pas de
//         hover natif sur tactile). Même liste d'items, capability-gated.
//     Masquée sur change-password (flux login). Les sessions kiosque ont leur
//     propre rail permanent (<KioskRail>).
// ============================================================================

import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { PlanningSignatureGate } from '@/pages/planning/PlanningSignatureGate';
import { useKiosk } from '@/lib/kiosk';
import { KioskRail } from '@/components/KioskRail';
import { Button } from '@/components/ui/button';
import { ChevronRight, LogOut, Menu, Settings, Sparkles, User, X } from 'lucide-react';
import { Capability } from '@magic/shared';
import { cn } from '@/lib/utils';
import { SIDEBAR_MODULES, type ModuleDef } from '@/lib/nav-modules';
import { useNewCandidaturesCount } from '@/lib/use-candidatures-count';
import { useMyOpenTasksCount } from '@/lib/use-my-tasks-count';
import { useNewNewsletterCount } from '@/lib/use-newsletter-count';
import { useNewPortageCount } from '@/lib/use-portage-count';
import { useNewCommandesCount } from '@/lib/use-commandes-count';
import { usePendingAbsencesCount } from '@/lib/use-pending-absences-count';
import { useNewFeedbackCount } from '@/lib/use-feedback-count';

// La liste des modules vient du registre partagé (source de vérité unique,
// commune avec les tuiles de l'accueil). Cf. apps/web/src/lib/nav-modules.ts.
const NAV_ITEMS = SIDEBAR_MODULES;

export default function AppShell() {
  const { me, hasCapability, signOut } = useAuth();
  const { isKiosk, slug } = useKiosk();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Badge « nouvelles candidatures » (statut 'nouveau') sur l'entrée Candidatures.
  const newCandidatures = useNewCandidaturesCount();
  // Badge « nouvelles tâches » (to-do MM, nominatif, non vues) sur l'entrée To-Do.
  const { count: myTasks, markSeen: markTasksSeen } = useMyOpenTasksCount();
  // Badge « inscriptions newsletter non traitées » (status 'new') sur l'entrée Newsletter.
  const newNewsletters = useNewNewsletterCount();
  // Badge « demandes de portage non traitées » (statut 'nouveau') sur l'entrée Portage.
  const newPortage = useNewPortageCount();
  // Badge « commandes cuisine reçues » (statut 'en_attente') sur l'entrée Production.
  const newCommandes = useNewCommandesCount();
  // Badge « demandes de congé en attente » (status 'pending') sur l'entrée Planning.
  const newAbsences = usePendingAbsencesCount();
  // Badge « retours produits à traiter » (handled=false) sur l'entrée Retours produits.
  const newFeedback = useNewFeedbackCount();
  const displayName =
    me?.profile.first_name || me?.profile.email?.split('@')[0] || 'utilisateur';

  // Accueil : pour une session kiosque, l'accueil est le tableau de bord de SON
  // PV (pas la liste multi-PV). Le logo et « Accueil » y renvoient.
  const homeTo = isKiosk && slug ? `/pv/${slug}` : '/';

  // Sidebar (utilisateurs normaux) : masquée sur change-password (flux login).
  // Désormais visible aussi sur l'accueil (demandé). Desktop = survol ;
  // mobile = tiroir hamburger. Les sessions kiosque ont un rail PERMANENT.
  const showSidebar = !isKiosk && !location.pathname.startsWith('/change-password');
  const visibleItems = NAV_ITEMS.filter((i) => !i.capability || hasCapability(i.capability as never));

  // Ferme le tiroir mobile à chaque changement de page.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Rendu partagé d'un item (desktop + tiroir mobile). onNavigate ferme le
  // tiroir au tap sur un lien interne.
  function renderNavItem(it: ModuleDef, onNavigate?: () => void) {
    const Icon = it.icon;
    const badge = it.key === 'candidatures' ? newCandidatures
      : it.key === 'todo' ? myTasks
      : it.key === 'newsletter' ? newNewsletters
      : it.key === 'portage' ? newPortage
      : it.key === 'production' ? newCommandes
      : it.key === 'planning' ? newAbsences
      : it.key === 'feedback' ? newFeedback
      : 0;
    const badgeNoun = it.key === 'todo' ? 'tâche'
      : it.key === 'newsletter' ? 'inscription'
      : it.key === 'portage' ? 'demande'
      : it.key === 'production' ? 'commande'
      : it.key === 'planning' ? 'demande'
      : it.key === 'feedback' ? 'retour'
      : 'candidature';
    const badgeAria = `${badge} nouvelle${badge > 1 ? 's' : ''} ${badgeNoun}${badge > 1 ? 's' : ''}`;
    const content = (
      <>
        <Icon className="h-4 w-4 shrink-0" />
        <span className="truncate">{it.label}</span>
        {badge > 0 && (
          <span
            className="ml-auto shrink-0 rounded-full bg-rose-600 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white text-center"
            style={{ minWidth: 18 }}
            aria-label={badgeAria}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </>
    );
    if (it.href) {
      return (
        <a
          key={it.label}
          href={it.href}
          target="_blank"
          rel="noreferrer"
          onClick={() => { if (it.key === 'todo') markTasksSeen(); onNavigate?.(); }}
          className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
        >
          {content}
        </a>
      );
    }
    return (
      <NavLink
        key={it.label}
        to={it.to!}
        end={it.to === '/'}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
            isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-accent',
          )
        }
      >
        {content}
      </NavLink>
    );
  }

  return (
    <div className={cn('min-h-screen', isKiosk && 'sm:pl-[4.75rem]')}>
      {/* Rail de navigation permanent des sessions kiosque (tactile). */}
      {isKiosk && <KioskRail slug={slug} />}
      <header className="border-b bg-background sticky top-0 z-30">
        <div className="container flex h-14 items-center justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            {/* Hamburger : ouvre le tiroir de navigation sur mobile uniquement. */}
            {showSidebar && (
              <Button
                size="sm" variant="ghost" className="px-2 sm:hidden"
                onClick={() => setMobileOpen(true)}
                aria-label="Ouvrir le menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <Link to={homeTo} className="flex items-center gap-2 font-semibold shrink-0">
              <Sparkles className="h-5 w-5 text-primary" />
              Magic
            </Link>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            {/* Session kiosque : compte partagé → pas de profil perso, pas de
                rôle affiché, pas de Réglages. */}
            {!isKiosk && (
              <>
                <Link
                  to="/me/profile"
                  className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground truncate hover:text-foreground transition-colors"
                  aria-label="Mon profil"
                  title="Mon profil"
                >
                  <User className="h-3.5 w-3.5" />
                  {displayName}
                </Link>
                <span className="hidden sm:inline rounded bg-muted px-1.5 py-0.5 text-xs">
                  {me?.profile.role}
                </span>
              </>
            )}
            {!isKiosk && hasCapability(Capability.USERS_READ_ALL) && (
              <Link to="/settings" aria-label="Réglages">
                <Button size="sm" variant="ghost" className="px-2 sm:px-3">
                  <Settings className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Réglages</span>
                </Button>
              </Link>
            )}
            <Button
              size="sm" variant="ghost" className="px-2 sm:px-3"
              onClick={() => void signOut()}
              aria-label="Déconnexion"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      {/* ─── Sidebar gauche au survol ───────────────────────────────────── */}
      {showSidebar && (
        <aside className="group fixed left-0 top-14 z-40 h-[calc(100vh-3.5rem)] hidden sm:flex">
          {/* Bande affordance : tab visible en permanence avec icône + chevron */}
          <div
            className="relative flex items-center justify-center w-7 bg-primary/90 text-primary-foreground
                       group-hover:w-7 group-hover:bg-primary
                       cursor-pointer transition-colors h-full shadow-md"
            title="Menu de navigation"
          >
            <div className="flex flex-col items-center gap-2 group-hover:opacity-0 transition-opacity">
              <Menu className="h-4 w-4" />
              <ChevronRight className="h-4 w-4 animate-pulse" />
            </div>
          </div>
          {/* Sidebar effective : slide-in au hover du group */}
          <nav
            className="absolute left-7 top-0 h-full w-60 -translate-x-[calc(100%+1.75rem)] opacity-0
                       group-hover:translate-x-0 group-hover:opacity-100
                       transition-all duration-200 ease-out
                       bg-background border-r shadow-2xl overflow-y-auto py-3 px-2 space-y-0.5"
            aria-label="Navigation rapide"
          >
            <div className="px-2 pb-2 text-[10px] font-semibold uppercase text-muted-foreground tracking-wide">
              Navigation rapide
            </div>
            {visibleItems.map((it) => renderNavItem(it))}
          </nav>
        </aside>
      )}

      {/* ─── Tiroir de navigation mobile ────────────────────────────────── */}
      {showSidebar && mobileOpen && (
        <div className="fixed inset-0 z-50 sm:hidden" role="dialog" aria-modal="true">
          {/* Backdrop : ferme au tap. */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          {/* Panneau coulissant. */}
          <nav
            className="absolute left-0 top-0 h-full w-72 max-w-[85%] bg-background border-r shadow-2xl
                       overflow-y-auto py-3 px-2 space-y-0.5 animate-in slide-in-from-left duration-200"
            aria-label="Navigation"
          >
            <div className="flex items-center justify-between px-2 pb-2">
              <span className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wide">
                Navigation
              </span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                aria-label="Fermer le menu"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {visibleItems.map((it) => renderNavItem(it, () => setMobileOpen(false)))}
          </nav>
        </div>
      )}

      <main>
        <Outlet />
      </main>

      {/* Rappel de signature du planning à la connexion (staff). */}
      <PlanningSignatureGate />
    </div>
  );
}
